<template>
  <div>
    <v-toolbar flat height="60px">
      <v-spacer v-if="$vuetify.breakpoint.smAndUp" />
      <v-list class="pa-0 pb-2" style="max-width: calc(100% - 50px)">
        <v-list-item class="pa-0">
          <v-list-item-avatar tile height="50px" :width="logoUrl ? '80px' : '40px'" class="my-0">
            <v-img v-if="logoUrl" contain :src="logoUrl" />
            <v-icon v-else large>mdi-account-group</v-icon>
          </v-list-item-avatar>
          <v-list-item-content class="py-0">
            <v-list-item-title>{{ displayName }}</v-list-item-title>
            <v-menu bottom max-height="300px" transition="slide-y-transition">
              <template v-if="$store.state.selectedActivity.displayParticipantCount" v-slot:activator="{ on, attrs }">
                <v-list-item-subtitle v-bind="attrs" v-on="on">
                  <strong>{{ $store.state.participantsTotalCount }}</strong>
                  {{ $t("participant.chat.participantsTotal") }}
                </v-list-item-subtitle>
              </template>
              <v-card width="370px">
                <v-list>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title> {{ $t("participant.chat.participantsHeader") }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-divider></v-divider>
                <v-list>
                  <v-list-item :key="i" v-for="(participant, i) in orderedParticipantList">
                    <v-list-item-avatar :color="participant.color" size="36">
                      <span>{{ participant.initials }}</span>
                    </v-list-item-avatar>
                    <v-list-item-title>{{
                      `${participant.firstName} ${participant.lastName} (${participant.username})`
                    }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-card>
            </v-menu>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <v-spacer />
      <v-badge
        v-if="!drawer && !isTeamZoomRoom"
        :content="numberOfUnreadChatMessages"
        :value="numberOfUnreadChatMessages > 0"
        bordered
        color="error"
        overlap
      >
        <v-btn color="primary" icon small @click.stop="$emit('click')">
          <v-icon>{{ icon || "mdi-forum-outline" }}</v-icon>
        </v-btn>
      </v-badge>
      <v-badge
        v-if="!drawer && isTeamZoomRoom"
        :content="$store.state.participantsTotalCount"
        :value="$store.state.participantsTotalCount > 0"
        bordered
        color="error"
        overlap
      >
        <v-btn color="primary" icon small @click.stop="$emit('click')">
          <v-icon>{{ icon || "mdi-video-outline" }}</v-icon>
        </v-btn>
      </v-badge>
      <v-btn v-if="!drawer" icon small @click.stop="$emit('click')" color="primary">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
      <v-btn v-if="drawer && !$vuetify.breakpoint.smAndUp" icon large @click.stop="$emit('click')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-slide-y-transition>
      <v-system-bar absolute style="top: 50px" v-if="latestNotif && showNotif" :window="$vuetify.breakpoint.lgAndUp">
        <img
          v-for="(notif, i) in latestNotif.withIcons"
          :key="i"
          alt="reaction"
          :src="notif.icon.type"
          class="emoji-icon mr-0"
        />
        <span v-if="latestNotif.children.length > 1" class="ml-2">
          <strong>{{ latestNotif.children.length }}</strong>
          <span class="ml-2" v-if="latestNotif.children[0].labelSeveral">{{
            latestNotif.children[0].labelSeveral
          }}</span>
          <span class="ml-2" v-else> {{ $t("live.activity.reacted.several") }} </span>
        </span>
        <span v-else class="ml-2">
          <strong>{{ latestNotif.children[0].who.username }}</strong>
          <span class="ml-2" v-if="latestNotif.children[0].label">{{ latestNotif.children[0].label }}</span>
          <span class="ml-2" v-else>{{ $t("live.activity.reacted.with") }}</span>
        </span>
      </v-system-bar>
    </v-slide-y-transition>
    <v-divider></v-divider>
  </div>
</template>

<script>
import { now } from "moment";

export default {
  name: "ParticipantHeader",
  props: {
    drawer: null,
    icon: null,
  },
  data() {
    return {
      hideNotifTimeout: null,
      hideParticipantNotifTimeout: null,
      showNotif: false,
      showParticipantNotif: false,
      lastSeenChatMessageId: -1,
    };
  },
  computed: {
    logoUrl() {
      if (this.$store.state.activityTeam) return this.$store.state.activityTeam.brandingLogoUrl || "";
      else return this.$store.state.event.brandingLogoUrl || "";
    },
    displayName() {
      if (this.$store.state.activityTeam) {
        return this.$store.state.activityTeam.name;
      }
      return this.$store.state.selectedActivity.contextDescription || this.$store.state.event.name;
    },
    latestNotif() {
      if (!this.$store.state.notifications.length) return null;
      const latest = this.$store.state.notifications[this.$store.state.notifications.length - 1];
      const children = [];
      const whos = [];
      for (let i = this.$store.state.notifications.length - 1; i >= 0; i--) {
        const n = this.$store.state.notifications[i];
        if (latest.date - n.date > 30000) break;
        if (n.type === latest.type) {
          if (whos.indexOf(n.who.id) === -1) {
            whos.push(n.who.id);
            children.push(n);
          }
        }
      }
      return {
        type: latest.type,
        date: latest.date,
        children: children,
        withIcons: children.filter((n) => n.icon),
      };
    },
    orderedParticipantList() {
      let participantsCopy = this.$store.state.participants.slice(0);
      participantsCopy.sort((a, b) => {
        let nameA = a.lastName.toLowerCase(),
          nameB = b.lastName.toLowerCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
      return participantsCopy;
    },
    lastChatMessageId() {
      let lastTextChatMessage = this.$store.state.textChatMessages.slice(-1).pop();
      if (!lastTextChatMessage) return -1;

      return lastTextChatMessage.id;
    },
    numberOfUnreadChatMessages() {
      if (this.lastChatMessageId === -1) return -1;
      if (this.lastSeenChatMessageId === -1) return this.$store.state.textChatMessages.length;

      return this.$store.state.textChatMessages.filter((chatMessage) => chatMessage.id > this.lastSeenChatMessageId)
        .length;
    },
    isTeamZoomRoom() {
      return !!(this.$store.state.activityTeam && this.$store.state.activityTeam.zoomMeetingNumber);
    }
  },
  watch: {
    drawer(val) {
      if (val === false) {
        this.lastSeenChatMessageId = this.lastChatMessageId;
      }
    },
    latestNotif(val) {
      if (this.hideNotifTimeout) clearTimeout(this.hideNotifTimeout);
      if (val) {
        this.showNotif = now() - val.date < 7000;
        this.hideNotifTimeout = setTimeout(() => (this.showNotif = false), 7000);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.emoji-icon {
  width: 24px;
  height: 24px;
}
</style>
